import { Head, Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import ContactLink from "@/components/ContactLink";
import Wordmark from "@/components/shared/Wordmark";

/**
 * In-app error page, rendered by ApplicationController#render_not_found
 * (and by resolvePage for an unknown component name). Mirrors the static
 * public/404.html and public/500.html so a missing record and a missing
 * route look the same.
 */
const MESSAGES = {
  404: {
    title: "Page not found",
    heading: ["That page wandered ", "off-shelf", "."],
    description:
      "We looked, but couldn't find what you're after. Maybe the link is old, or the page got moved.",
  },
  500: {
    title: "Something went sideways",
    heading: ["Whoops — a book ", "tipped over", "."],
    description:
      "Something on our end went sideways. Nothing you did, and nothing's lost — we're picking up the pages.",
  },
};

export default function Error({ status }) {
  const code = MESSAGES[status] ? status : 500;
  const { title, heading, description } = MESSAGES[code];
  const { routes } = usePage().props;
  const [before, accent, after] = heading;

  return (
    <>
      <Head title={title} />
      <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
        <div className="w-full max-w-xl text-center">
          <Wordmark href={routes.home} className="text-lg" />

          <BookIllustration className="mx-auto mb-7 mt-10 h-auto w-36 sm:w-44" />

          <span className="mb-5 inline-block rounded-full border border-accent/25 bg-accent/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] text-accent">
            Error {code}
          </span>

          <h1 className="mb-4 font-display text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl">
            {before}
            <span className="whitespace-nowrap text-primary">{accent}</span>
            {after}
          </h1>

          <p className="mx-auto mb-8 max-w-md text-lg leading-relaxed text-muted-foreground">{description}</p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild>
              <Link href={routes.dashboard || routes.home}>{routes.dashboard ? "Go to your dashboard" : "Back to safety"}</Link>
            </Button>
            <Button variant="outline" asChild>
              <ContactLink>Tell us what you were looking for</ContactLink>
            </Button>
          </div>
        </div>
      </main>
    </>
  );
}

// Open book under a magnifying glass, same drawing as public/404.html.
function BookIllustration({ className }) {
  return (
    <svg className={className} viewBox="0 0 200 160" fill="none" aria-hidden="true">
      <ellipse cx="100" cy="142" rx="62" ry="5" className="fill-foreground" opacity="0.06" />

      <g className="stroke-foreground" strokeWidth="2" strokeLinejoin="round">
        <path d="M 26 50 Q 100 38 100 44 L 100 124 Q 100 116 26 124 Z" className="fill-background" />
        <path d="M 174 50 Q 100 38 100 44 L 100 124 Q 100 116 174 124 Z" className="fill-background" />
        <line x1="100" y1="44" x2="100" y2="124" />
      </g>

      <g className="stroke-foreground" strokeWidth="1.5" opacity="0.4">
        <line x1="38" y1="62" x2="90" y2="58" />
        <line x1="38" y1="74" x2="90" y2="70" />
        <line x1="38" y1="86" x2="84" y2="82" />
        <line x1="38" y1="98" x2="88" y2="94" />
        <line x1="110" y1="58" x2="162" y2="62" />
        <line x1="110" y1="70" x2="162" y2="74" />
        <line x1="110" y1="82" x2="156" y2="86" />
        <line x1="110" y1="94" x2="160" y2="98" />
      </g>

      <g transform="rotate(-12 134 78)">
        <circle cx="134" cy="78" r="22" className="fill-background stroke-accent" fillOpacity="0.5" strokeWidth="3" />
        <line x1="150" y1="94" x2="166" y2="112" className="stroke-accent" strokeWidth="5" strokeLinecap="round" />
        <path d="M 122 70 q 4 -8 14 -8" className="stroke-white" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      </g>

      <text x="60" y="40" className="fill-primary font-display" fontSize="22" fontWeight="600">
        ?
      </text>
    </svg>
  );
}
